<script setup lang="ts">
import { FontSetting } from "@/models/FontSetting";
import { SelectOption } from "@/models/frontend-only/SelectOption";
import ToolbarFontButton from "./ToolbarFontButton.vue";
import ToolbarButtonDropdown from "./ToolbarButtonDropdown.vue";

const data = defineModel<{
  headings: FontSetting;
  lyrics: FontSetting;
  direction?: FontSetting;
  chords?: FontSetting;
}>();

const props = defineProps<{
  enableChords?: boolean;
}>();

const { t } = useI18n();

const dark = useDarkModeStore();
const applyToPart = ref<"headings" | "lyrics" | "chords" | "directions">(
  "headings",
);
const showingMenu = ref("");

const btnFamily = ref<InstanceType<typeof ToolbarFontButton>>();
const btnSize = ref<InstanceType<typeof ToolbarFontButton>>();
const btnColor = ref<InstanceType<typeof ToolbarFontButton>>();
const btnHighlight = ref<InstanceType<typeof ToolbarFontButton>>();

const applyOptions = computed<SelectOption[]>(() => [
  {
    label: t("SV.LIT_SongHeadings"),
    value: "headings",
  },
  ...(props.enableChords
    ? [
        {
          label: t("S.LIT_Chords"),
          value: "chords",
        },
        {
          label: t("SV.LIT_Annotations"),
          value: "directions",
        },
      ]
    : []),
  {
    label: t("S.LIT_Lyrics"),
    value: "lyrics",
  },
]);

const selectedOption = computed(
  () =>
    applyOptions.value.find((o) => o.value === applyToPart.value)?.label || "",
);

const settings = computed<FontSetting | null>(
  () => (data.value && (data.value as any)[applyToPart.value]) || null,
);

const clearTitle = computed(
  () => t("SV.LIT_ClearFontSettingsFor", {
    setting: selectedOption.value,
  })
)

onMounted(() => {
  emitter.on("resetFontStylesConfirmed", () => resetSettings(true));
});

onBeforeUnmount(() => {
  emitter.off("resetFontStylesConfirmed");
});

function resetSettings(confirm?: boolean) {
  if (!confirm) {
    const modals = useModalStore();
    modals.showModal("ConfirmClearStylesModal");

    return;
  }

  showingMenu.value = "";

  (data.value as any)[applyToPart.value].color = AvailableTextColors[0].name;
  (data.value as any)[applyToPart.value].highlight =
    AvailableHighlightColors[0].name;
  (data.value as any)[applyToPart.value].family = AvailableFontFamilies[0].type;
  (data.value as any)[applyToPart.value].size = "16";

  const toast = useToastStore();
  toast.successToast(t("SV.LIT_FontSettingsHaveBeenReset"));
}

function applyFamily(fontFamilyType: string) {
  if (settings.value) {
    settings.value.family = fontFamilyType;
  }
}

const activeFontColor = computed(() => {
  const color = AvailableTextColors.find(
    (c) => c.name === settings.value?.color,
  );
  if (color) {
    if (dark.isDarkModeEnabled) {
      return color.dark;
    } else {
      return color.light;
    }
  }
});

const activeHighlightColor = computed(() => {
  let color;
  if (settings.value?.highlight === "transparent") {
    color = AvailableHighlightColors[1];
  } else {
    color = AvailableHighlightColors.find(
      (c) => c.name === settings.value?.highlight,
    );
  }
  if (color) {
    if (dark.isDarkModeEnabled) {
      return color.dark;
    } else {
      return color.light;
    }
  }
});

const dropdown = ref<InstanceType<typeof ToolbarButtonDropdown>>();
const contentEl = computed(() => dropdown.value?.dropdown?.contents?.$el);

watch(applyToPart, () => {
  (window as any).dataLayer.push({
    font_setting_section: applyToPart.value
  })
})

onMounted(loadFonts);
function loadFonts() {
  AvailableFontFamilies.forEach((f) =>
    loadFont(`sheetmusic-font-${f.type}`, f.url),
  );
}
</script>

<template>
  <ToolbarButtonGroup
    :title="$t('SV.LIT_Font')"
    class="toolbar-font-settings block"
  >
    <ToolbarButtonDropdown
      ref="dropdown"
      :title="$t('SV.LIT_Font')"
      label=""
      icon="fa-font"
      class="block"
      disable-content-auto-close
      forced-content-width="220px"
      @open="showingMenu = ''"
    >
      <ToolbarButtonDropdownHeader>{{ $t('SV.LIT_FontSettings') }}</ToolbarButtonDropdownHeader>
      <div class="toolbar-font-settings-popover mb-2 grid gap-1">
        <div class="flex items-center gap-1">
          <FormSelect
            v-model="applyToPart"
            :options="applyOptions"
            label="Apply to"
            hide-label
            class="flex-1"
            :class="enableChords ? 'ga-chordsheet-font-settings-select' : 'ga-lyrics-font-settings-select'"
            :data-content-type="enableChords ? 'ChordSheet' : 'Lyrics'"
            data-content-action="applyFontSettingsSection"
          ></FormSelect>

          <ToolbarFontButton
            :title="clearTitle"
            icon="fa-light fa-eraser"
            @click="resetSettings()"
          />
        </div>
        <div
          v-if="settings"
          class="flex gap-1"
        >
          <ToolbarFontButton
            ref="btnFamily"
            :title="$t('S.LIT_FontFamilyTC')"
            icon="fa-solid fa-font"
            :active="showingMenu === 'font-family'"
            @click="showingMenu = 'font-family'"
            @mouseenter="showingMenu = 'font-family'"
          />
          <ToolbarFontButton
            ref="btnSize"
            :title="$t('S.LIT_FontSizeTC')"
            icon="fa-light fa-text-size"
            :active="showingMenu === 'font-size'"
            @click="showingMenu = 'font-size'"
            @mouseenter="showingMenu = 'font-size'"
          />
          <ToolbarFontButton
            ref="btnColor"
            :title="$t('SV.LIT_FontColor')"
            icon="fa-light fa-font"
            :color="activeFontColor"
            :active="showingMenu === 'font-color'"
            @click="showingMenu = 'font-color'"
            @mouseenter="showingMenu = 'font-color'"
          />
          <ToolbarFontButton
            ref="btnHighlight"
            :title="$t('SV.LIT_HighlightColor')"
            icon="fa-light fa-highlighter"
            :class="{ hidden: applyToPart === 'lyrics' }"
            :color="activeHighlightColor"
            :active="showingMenu === 'highlight-color'"
            @click="showingMenu = 'highlight-color'"
            @mouseenter="showingMenu = 'highlight-color'"
          />
        </div>
      </div>

      <div
        v-if="showingMenu === 'font-family' && contentEl"
        class="extended-options"
      >
        <ToolbarButtonDropdownHeader>{{ $t('S.LIT_FontFamilyTC') }}</ToolbarButtonDropdownHeader>
        <ToolbarButtonDropdownItem
          v-for="f of AvailableFontFamilies"
          :key="f.type"
          :active="(settings && settings.family === f.type) || false"
          data-content-action="applyFontFamily"
          :data-content-type="enableChords ? 'ChordSheet' : 'Lyrics'"
          :data-sheet-font-family="f.type"
          class="ga-apply-font-family"
          @click="applyFamily(f.type)"
          ><span :style="`font-family:${f.fontFamilyName}`">{{
            f.type
          }}</span></ToolbarButtonDropdownItem
        >
      </div>
      <div
        v-if="showingMenu === 'font-size' && settings"
        class="extended-options"
      >
        <ToolbarButtonDropdownHeader>{{
          $t("S.LIT_FontSizeTC")
        }}</ToolbarButtonDropdownHeader>
        <FormSlider
          v-model="settings.size"
          :class="enableChords ? 'ga-chordsheet-settings-font-size' : 'ga-lyrics-settings-font-size'"
          name="FontSize"
          data-content-action="applyFontSize"
          :data-content-type="enableChords ? 'ChordSheet' : 'Lyrics'"
          :label="$t('S.LIT_FontSizeTC')"
          :increase-label="$t('SV.LIT_IncreaseFontSize')"
          :decrease-label="$t('SV.LIT_DecreaseFontSize')"
          min="10"
          max="32"
          hide-label
        />
      </div>
      <div
        v-if="showingMenu === 'font-color' && settings"
        class="extended-options"
      >
        <ToolbarButtonDropdownHeader>{{ $t('SV.LIT_FontColor') }}</ToolbarButtonDropdownHeader>
        <ColorSelection
          v-model="settings.color"
          class="ga-apply-font-color"
          :options="AvailableTextColors"
          data-content-action="applyFontColor"
          :data-content-type="enableChords ? 'ChordSheet' : 'Lyrics'"
        />
      </div>
      <div
        v-if="
          showingMenu === 'highlight-color' &&
          settings &&
          applyToPart !== 'lyrics'
        "
        class="extended-options"
      >
        <ToolbarButtonDropdownHeader
          >{{ $t('SV.LIT_HighlightColor') }}</ToolbarButtonDropdownHeader
        >
        <ColorSelection
          v-model="settings.highlight"
          class="ga-apply-highlight-color"
          data-content-action="applyHighlightColor"
          :data-content-type="enableChords ? 'ChordSheet' : 'Lyrics'"
          :options="AvailableHighlightColors"
        />
      </div>
    </ToolbarButtonDropdown>
  </ToolbarButtonGroup>
</template>

<style scoped lang="postcss">
.extended-options {
  @apply rounded-lg  bg-gray-100 p-2;
}
:deep() {
  input[type="range"] {
    @apply w-24 bg-gray-200;
  }
}
</style>
